import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Search, Network, ChevronRight } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import Breadcrumb from '../components/Breadcrumb';
import { api } from '../api';

const ratingTone = (r) => {
  if (r == null) return 'bg-[#F0E7D2] text-[#3A4A44]';
  if (r >= 4) return 'bg-[#4A7C5E] text-white';
  if (r >= 3.5) return 'bg-[#7BA88E] text-white';
  if (r >= 2.5) return 'bg-[#D4A85C] text-[#1A2622]';
  return 'bg-[#D58668] text-white';
};

export default function ChainsPage() {
  const [q, setQ] = useState('');
  const [page, setPage] = useState(0);
  const [data, setData] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const LIMIT = 40;

  useEffect(() => { setPage(0); }, [q]);

  useEffect(() => {
    const t = setTimeout(() => {
      setLoading(true);
      api.chains({ q: q || undefined, limit: LIMIT, offset: page * LIMIT }).then((d) => { setData(d); setLoading(false); }).catch(() => setLoading(false));
    }, 200);
    return () => clearTimeout(t);
  }, [q, page]);

  const totalPages = Math.max(1, Math.ceil((data.total || 0) / LIMIT));

  return (
    <div className="min-h-screen">
      <Navbar />
      <main className="max-w-[1280px] mx-auto px-6 lg:px-8 pt-6 pb-10">
        <Breadcrumb items={[{ label: 'Home', to: '/' }, { label: 'Chains' }]} />

        <div className="mt-6">
          <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md border border-[#4A7C5E]/30 bg-white">
            <Network size={12} className="text-[#4A7C5E]"/>
            <span className="text-[11px] font-semibold uppercase tracking-wider text-[#4A7C5E]">CMS Chains</span>
          </div>
          <h1 className="mt-4 font-display text-4xl font-semibold text-[#1A2622] tracking-tight">Chain organizations</h1>
          <p className="mt-3 text-[15px] text-[#3A4A44] max-w-3xl">Multi-facility operators recognized in the CMS Ownership dataset, with aggregated rating across their portfolio.</p>
        </div>

        <div className="mt-7 h-px bg-[#E5DCC4]"/>

        <div className="mt-6 relative max-w-2xl">
          <Search size={14} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#7A857F]"/>
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search chains by name" className="w-full bg-white border border-[#E5DCC4] rounded-lg h-11 pl-10 pr-3 text-[13px] text-[#1A2622] placeholder:text-[#A8B0AA] outline-none focus:border-[#4A7C5E]"/>
        </div>

        <div className="mt-7 flex items-center justify-between">
          <span className="text-[11px] font-semibold uppercase tracking-wider text-[#4A7C5E]">All chains</span>
          <span className="text-xs text-[#7A857F]">{loading ? 'loading…' : `Showing ${page * LIMIT + 1}–${Math.min((page + 1) * LIMIT, data.total)} of ${data.total.toLocaleString()}`}</span>
        </div>

        <ul className="mt-4 space-y-2">
          {data.items.map((c, i) => (
            <li key={c.slug}>
              <Link to={`/chains/${c.slug}`} className="group flex items-center justify-between gap-3 px-4 py-3 rounded-lg bg-white border border-[#E5DCC4] hover:border-[#4A7C5E] transition-colors">
                <div className="flex items-center gap-3.5 min-w-0 flex-1">
                  <span className="text-[11px] font-mono text-[#A8B0AA] w-6">{String(i+1).padStart(2,'0')}</span>
                  <div className="w-10 h-10 rounded-md bg-[#F0E7D2] flex items-center justify-center shrink-0"><Network size={16} className="text-[#4A7C5E]"/></div>
                  <div className="min-w-0">
                    <div className="text-[14px] font-medium text-[#1A2622] truncate group-hover:text-[#2D5F4F]">{c.name}</div>
                    <div className="text-[11px] text-[#7A857F] mt-0.5">{c.facilities} facilities</div>
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <span className={`inline-flex w-9 h-8 rounded-md ${ratingTone(c.rating)} text-[12.5px] font-semibold items-center justify-center`}>{c.rating != null ? c.rating.toFixed(1) : '—'}</span>
                  <ChevronRight size={14} className="text-[#A8B0AA] group-hover:text-[#4A7C5E] group-hover:translate-x-0.5 transition-all"/>
                </div>
              </Link>
            </li>
          ))}
        </ul>

        {data.total > LIMIT && (
          <div className="mt-6 flex items-center justify-between text-[13px] text-[#5A6660]">
            <button onClick={() => setPage(Math.max(0, page - 1))} disabled={page === 0} className="px-3.5 py-1.5 rounded-md border border-[#E5DCC4] bg-white hover:border-[#4A7C5E] disabled:opacity-40 disabled:cursor-not-allowed">Previous</button>
            <span>Page {page + 1} of {totalPages.toLocaleString()}</span>
            <button onClick={() => setPage(page + 1)} disabled={page + 1 >= totalPages} className="px-3.5 py-1.5 rounded-md border border-[#E5DCC4] bg-white hover:border-[#4A7C5E] disabled:opacity-40 disabled:cursor-not-allowed">Next</button>
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}
